import { useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { Q_MAP_VIEW, Q_VENUE } from "constant"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { convertMapViewObjectToPath } from "utils/map"

const DEFAULT_MIN_ZOOM = 19
export interface UseMapViewAndVenueTrackingProps {
  /**
   * Minimum zoom level to start tracking
   * @default 19
   */
  minZoom?: number
}

/**
 * Tracks map view state and syncs to URL
 *
 * Features:
 * - Updates URL with map view (center, zoom, pitch, bearing)
 * - Auto-detects venue changes on zoom
 * - Tracks pan/drag within same venue
 *
 * @example
 * // Basic usage
 * useMapViewAndVenueTracking()
 *
 * @example
 * // Custom configuration
 * useMapViewAndVenueTracking({ minZoom: 18 })
 */
export const useMapViewAndVenueTracking = ({
  minZoom = DEFAULT_MIN_ZOOM,
}: UseMapViewAndVenueTrackingProps = {}) => {
  const navigate = useNavigate()
  const { indoorRef, mapLoaded } = useIndoorMap()

  const handleMapViewAndVenueQuery = useCallback(() => {
    /**
     * Use URLSearchParams to manage the current query string instead of
     * React Router's searchParams.
     *
     * This prevents bound handler functions from using stale query values,
     * which can cause unexpected behavior during navigation.
     */
    const searchParams = new URLSearchParams(window.location.search)
    const indoorMap = indoorRef.current

    if (!indoorMap) return

    const shouldTrackMapView = indoorMap.map.getZoom() >= minZoom

    if (!shouldTrackMapView) return

    const currentMapView = indoorMap.getCurrentMapView()

    const mapViewPath = convertMapViewObjectToPath(currentMapView)
    searchParams.set(Q_MAP_VIEW, mapViewPath)
    const venueIdInView = indoorMap.findVenueInView()?.venueId

    if (venueIdInView) {
      searchParams.set(Q_VENUE, venueIdInView)
    }

    const currentPathname = window.location.pathname

    navigate(`${currentPathname}?${searchParams.toString()}`, {
      replace: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indoorRef, minZoom])

  // Setup and cleanup map event listeners
  useEffect(() => {
    const indoorMap = indoorRef.current
    if (!mapLoaded || !indoorMap) return

    indoorMap.map.on("zoomend", handleMapViewAndVenueQuery)
    indoorMap.map.on("moveend", handleMapViewAndVenueQuery)

    return () => {
      indoorMap.map.off("zoomend", handleMapViewAndVenueQuery)
      indoorMap.map.off("moveend", handleMapViewAndVenueQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indoorRef, mapLoaded, handleMapViewAndVenueQuery])
}
